import { BaseSyntheticEvent, useRef } from 'react';
import { useAlert } from 'react-alert';
import { Control, FormState, UseFormRegister } from 'react-hook-form';
import { useSelector } from 'react-redux';
import I18n from 'i18n-js';

import { updateUser } from '@lootboy/lib/actions/UserActions';
import { TPickerItem } from '@lootboy/lib/forms/common';
import {
  ERegisterUserDetailsFormField,
  TRegisterUserDetailsFieldValues,
  useRegisterUserDetailsForm,
} from '@lootboy/lib/forms/useRegisterUserDeatilsForm';
import { registrationNotCompletedSelector } from '@lootboy/lib/selectors';
import { TGender } from '@lootboy/lib/types/user';
import { toISODateString } from '@lootboy/lib/utils/dateAndTime';

import Alert from '~/components/Alert';

import countries from '~/constants/countries';
import { FEMALE, MALE, UNKNOWN_GENDER } from '~/constants/genders';
import { useClosePopup } from '~/hooks/useClosePopup';
import { CountryOption } from '~/popups/UserDetailsPopup/CountryOption';
import { userDetailsSelector } from '~/redux/selectors/user';
import { TAppStore, useAppDispatch } from '~/redux/store/useAppDispatch';
import { parseLootBoyError } from '~/utilities/errors';

const countryOptions = Object.entries(countries).map(([countryCode, name]) => ({
  value: countryCode,
  name,
  label: (
    <CountryOption
      name={name}
      countryCode={countryCode}
    />
  ),
}));

type TUseUserDetailsState = {
  form: {
    control: Control<TRegisterUserDetailsFieldValues>;
    onSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
    register: UseFormRegister<TRegisterUserDetailsFieldValues>;
    isSubmitEnabled: boolean;
    errors: FormState<TRegisterUserDetailsFieldValues>['errors'];
  };
  selectOptions: {
    gender: Array<TPickerItem<TGender>>;
    country: Array<{ label: JSX.Element; value: string }>;
  };
};

export function useUserDetails(): TUseUserDetailsState {
  const alert = useAlert();
  const closePopup = useClosePopup();
  const dispatch = useAppDispatch();
  const registrationNotCompleted = useSelector<TAppStore, boolean>(
    registrationNotCompletedSelector,
  );
  const initialRegistrationNotCompleted = useRef(registrationNotCompleted);
  const userDetails = useSelector(userDetailsSelector);
  const genderOptions: Array<{ label: string; value: TGender }> = [
    {
      label: I18n.t('MenuView_profile_gender_female'),
      value: FEMALE,
    },
    {
      label: I18n.t('MenuView_profile_gender_male'),
      value: MALE,
    },
    { label: I18n.t('MenuView_profile_gender_no'), value: UNKNOWN_GENDER },
  ];

  const {
    control,
    onFormSubmit,
    formState: { errors },
    register,
    isSubmitEnabled,
  } = useRegisterUserDetailsForm(
    async (formValues) => {
      try {
        await dispatch(
          updateUser({
            birthday: toISODateString(formValues.birthDate),
            country: formValues.country.value,
            gender:
              formValues.gender.value === 'none'
                ? null
                : formValues.gender.value,
            username: formValues.username,
          }),
        );
        if (!initialRegistrationNotCompleted.current) {
          alert.success(
            <Alert title={I18n.t('common_updated_successfully')} />,
          );
          closePopup();
        }
      } catch (error) {
        const { message } = parseLootBoyError(
          error,
          I18n.t('common_update_failed'),
        );
        alert.error(
          <Alert
            title={I18n.t('common_error')}
            body={message}
          />,
        );
      }
    },
    {
      defaultValues: {
        // @ts-ignore aaa
        [ERegisterUserDetailsFormField.BIRTH_DATE]: userDetails.birthday
          ? new Date(userDetails.birthday)
          : null,
        // @ts-ignore we can use ReactNode as a label on web
        [ERegisterUserDetailsFormField.COUNTRY]: userDetails.country
          ? countryOptions.find(({ value }) => value === userDetails.country) ||
            null
          : null,
        // @ts-ignore null is user to signal that user chose not to specify their gender
        [ERegisterUserDetailsFormField.GENDER]:
          userDetails.gender || userDetails.gender === null
            ? genderOptions.find(({ value }) => value === userDetails.gender) ||
              null
            : null,
        [ERegisterUserDetailsFormField.USERNAME]: userDetails.username ?? '',
      },
    },
  );

  return {
    form: {
      control,
      onSubmit: onFormSubmit,
      register,
      isSubmitEnabled,
      errors,
    },
    selectOptions: {
      gender: genderOptions,
      country: countryOptions,
    },
  };
}
